import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJWTAuth } from '../context/JWTAuthContext';
import { publicApi } from '../utils/jwtApiService';
import './SignupPage.css';

const SignupPage = ({ onClose, onSwitchToLogin }) => {
  const navigate = useNavigate();
  const auth = useJWTAuth();
  
  // 폼 데이터 상태
  const [formData, setFormData] = useState({
    user_id: '',
    password: '',
    confirmPassword: '',
    email: '',
    nickname: ''
  });

  // 유효성 검사 상태
  const [validation, setValidation] = useState({
    user_id: { valid: null, message: '' },
    password: { valid: null, message: '' },
    confirmPassword: { valid: null, message: '' },
    email: { valid: null, message: '' },
    nickname: { valid: null, message: '' }
  });

  // UI 상태
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [isCheckingNickname, setIsCheckingNickname] = useState(false);

  // 비밀번호 유효성 검사 함수
  const validatePassword = (password) => {
    if (!password) {
      return { valid: false, message: '비밀번호를 입력해주세요.' };
    }

    if (password.length < 12) {
      return { valid: false, message: '비밀번호는 최소 12자 이상이어야 합니다.' };
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase) {
      return { valid: false, message: '대문자를 포함해야 합니다.' };
    }
    if (!hasLowerCase) {
      return { valid: false, message: '소문자를 포함해야 합니다.' };
    }
    if (!hasNumber) {
      return { valid: false, message: '숫자를 포함해야 합니다.' };
    }
    if (!hasSpecialChar) {
      return { valid: false, message: '특수문자를 포함해야 합니다.' };
    }

    return { valid: true, message: '안전한 비밀번호입니다.' };
  };

  // 입력값 변경 처리
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // 실시간 유효성 검사
    validateField(field, value);
  };

  // 필드별 유효성 검사
  const validateField = (field, value) => {
    let valid = null;
    let message = '';

    switch (field) {
      case 'user_id':
        if (!value) {
          valid = false;
          message = '아이디를 입력해주세요.';
        } else if (value.length < 4 || value.length > 50) {
          valid = false;
          message = '아이디는 4-50자 사이여야 합니다.';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
          valid = false;
          message = '아이디는 영문, 숫자, _, - 만 사용 가능합니다.';
        } else {
          valid = true;
          message = '사용 가능한 아이디입니다.';
        }
        break;

      case 'password':
        const passwordValidation = validatePassword(value);
        valid = passwordValidation.valid;
        message = passwordValidation.message;
        
        // 확인 비밀번호도 같이 검사
        if (formData.confirmPassword && value !== formData.confirmPassword) {
          setValidation(prev => ({
            ...prev,
            confirmPassword: { valid: false, message: '비밀번호가 일치하지 않습니다.' }
          }));
        } else if (formData.confirmPassword && value === formData.confirmPassword) {
          setValidation(prev => ({
            ...prev,
            confirmPassword: { valid: true, message: '비밀번호가 일치합니다.' }
          }));
        }
        break;

      case 'confirmPassword':
        if (!value) {
          valid = false;
          message = '비밀번호 확인을 입력해주세요.';
        } else if (value !== formData.password) {
          valid = false;
          message = '비밀번호가 일치하지 않습니다.';
        } else {
          valid = true;
          message = '비밀번호가 일치합니다.';
        }
        break;

      case 'email':
        if (!value) {
          valid = false;
          message = '이메일을 입력해주세요.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          valid = false;
          message = '올바른 이메일 형식이 아닙니다.';
        } else {
          valid = true;
          message = '사용 가능한 이메일입니다.';
        }
        break;

      case 'nickname':
        if (!value) {
          valid = false;
          message = '닉네임을 입력해주세요.';
        } else if (value.length < 2 || value.length > 20) {
          valid = false;
          message = '닉네임은 2-20자 사이여야 합니다.';
        } else {
          valid = true;
          message = '닉네임 중복 확인이 필요합니다.';
        }
        break;

      default:
        break;
    }

    setValidation(prev => ({
      ...prev,
      [field]: { valid, message }
    }));
  };

  // 닉네임 중복 체크
  const handleNicknameCheck = async () => {
    if (!formData.nickname || validation.nickname.valid === false) {
      return;
    }

    setIsCheckingNickname(true);
    try {
      const result = await publicApi.checkNickname(formData.nickname);

      if (result.success && result.data) {
        setValidation(prev => ({
          ...prev,
          nickname: {
            valid: result.data.available,
            message: result.data.message
          }
        }));
      } else {
        setValidation(prev => ({
          ...prev,
          nickname: {
            valid: false,
            message: result.message || '닉네임 확인 중 오류가 발생했습니다.'
          }
        }));
      }
    } catch (error) {
      console.error('닉네임 체크 오류:', error);
      setValidation(prev => ({
        ...prev,
        nickname: {
          valid: false,
          message: '닉네임 확인 중 오류가 발생했습니다.'
        }
      }));
    } finally {
      setIsCheckingNickname(false);
    }
  };

  // 전체 폼 유효성 검사
  const isFormValid = () => {
    return Object.values(validation).every(field => field.valid === true) &&
           Object.values(formData).every(value => value.trim() !== '');
  };

  // 회원가입 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setSubmitMessage({
        type: 'error',
        text: '모든 필드를 올바르게 입력해주세요.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const signupData = {
        user_id: formData.user_id.trim(),
        password: formData.password,
        email: formData.email.trim(),
        nickname: formData.nickname.trim()
      };

      const result = await publicApi.signup(signupData);

      if (result.success) {
        setSubmitMessage({
          type: 'success',
          text: '회원가입이 완료되었습니다! 로그인 페이지로 이동합니다.'
        });

        // 2초 후 로그인 페이지로 전환
        setTimeout(() => {
          if (onSwitchToLogin && typeof onSwitchToLogin === 'function') {
            onSwitchToLogin();
          } else {
            navigate('/login');
          }
        }, 2000);
      } else {
        setSubmitMessage({
          type: 'error',
          text: result.message
        });
      }
    } catch (error) {
      console.error('회원가입 오류:', error);
      setSubmitMessage({
        type: 'error',
        text: '회원가입 중 오류가 발생했습니다.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <h2>회원가입</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form className="signup-form" onSubmit={handleSubmit}>
          {/* 아이디 입력 */}
          <div className="form-group">
            <label htmlFor="user_id">아이디 *</label>
            <input
              id="user_id"
              type="text"
              value={formData.user_id}
              onChange={(e) => handleInputChange('user_id', e.target.value)}
              placeholder="4-50자의 영문, 숫자, _, - 만 사용 가능"
              className={validation.user_id.valid === null ? '' : 
                        validation.user_id.valid ? 'valid' : 'invalid'}
            />
            {validation.user_id.message && (
              <span className={`validation-message ${validation.user_id.valid ? 'success' : 'error'}`}>
                {validation.user_id.message}
              </span>
            )}
          </div>

          {/* 비밀번호 입력 */}
          <div className="form-group">
            <label htmlFor="password">비밀번호 *</label>
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder="대소문자, 숫자, 특수문자 포함 12자리 이상"
              className={validation.password.valid === null ? '' : 
                        validation.password.valid ? 'valid' : 'invalid'}
            />
            {validation.password.message && (
              <span className={`validation-message ${validation.password.valid ? 'success' : 'error'}`}>
                {validation.password.message}
              </span>
            )}
          </div>

          {/* 비밀번호 확인 */}
          <div className="form-group">
            <label htmlFor="confirmPassword">비밀번호 확인 *</label>
            <input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              placeholder="비밀번호를 다시 입력해주세요"
              className={validation.confirmPassword.valid === null ? '' : 
                        validation.confirmPassword.valid ? 'valid' : 'invalid'}
            />
            {validation.confirmPassword.message && (
              <span className={`validation-message ${validation.confirmPassword.valid ? 'success' : 'error'}`}>
                {validation.confirmPassword.message}
              </span>
            )}
          </div>

          {/* 이메일 입력 */}
          <div className="form-group">
            <label htmlFor="email">이메일 *</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your@example.com"
              className={validation.email.valid === null ? '' : 
                        validation.email.valid ? 'valid' : 'invalid'}
            />
            {validation.email.message && (
              <span className={`validation-message ${validation.email.valid ? 'success' : 'error'}`}>
                {validation.email.message}
              </span>
            )}
          </div>

          {/* 닉네임 입력 */}
          <div className="form-group">
            <label htmlFor="nickname">닉네임 *</label>
            <div className="nickname-input-group">
              <input
                id="nickname"
                type="text"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                placeholder="2-20자의 닉네임"
                className={validation.nickname.valid === null ? '' : 
                          validation.nickname.valid ? 'valid' : 'invalid'}
              />
              <button
                type="button"
                className="nickname-check-btn"
                onClick={handleNicknameCheck}
                disabled={!formData.nickname || validation.nickname.valid === false || isCheckingNickname}
              >
                {isCheckingNickname ? '확인중...' : '중복확인'}
              </button>
            </div>
            {validation.nickname.message && (
              <span className={`validation-message ${validation.nickname.valid ? 'success' : 'error'}`}>
                {validation.nickname.message}
              </span>
            )}
          </div>

          {/* 제출 메시지 */}
          {submitMessage.text && (
            <div className={`submit-message ${submitMessage.type}`}>
              {submitMessage.text}
            </div>
          )}

          {/* 제출 버튼 */}
          <button
            type="submit"
            className="signup-submit-btn"
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? '가입 중...' : '회원가입'}
          </button>

          {/* 로그인 링크 */}
          <div className="switch-auth">
            이미 계정이 있으신가요?{' '}
            <button
              type="button"
              className="switch-link"
              onClick={() => {
                if (onSwitchToLogin && typeof onSwitchToLogin === 'function') {
                  onSwitchToLogin();
                } else {
                  navigate('/login');
                }
              }}
            >
              로그인
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;